import React from 'react';

function cn(...classes: Array<string | false | null | undefined>): string {
  return classes.filter(Boolean).join(' ');
}

// ── Card ─────────────────────────────────────────────────────────────────
export interface CardProps {
  title?: React.ReactNode;
  description?: React.ReactNode;
  children?: React.ReactNode;
  className?: string;
}

export function Card({ title, description, children, className = '' }: CardProps) {
  return (
    <section className={cn('mb-3', className)}>
      {(title || description) && (
        <div className="mb-2">
          {title && <h2 className="text-lg font-semibold text-zinc-200">{title}</h2>}
          {description && <div className="text-sm text-zinc-400 mt-0.5">{description}</div>}
        </div>
      )}
      <div className="bg-zinc-900/70 border border-zinc-800 rounded-lg p-3">{children}</div>
    </section>
  );
}

// ── Section ──────────────────────────────────────────────────────────────
export interface SectionProps {
  children?: React.ReactNode;
  className?: string;
}

export function Section({ children, className = '' }: SectionProps) {
  return (
    <div className={cn('bg-zinc-900/70 border border-zinc-800 rounded-lg p-3', className)}>
      {children}
    </div>
  );
}

// ── Button ───────────────────────────────────────────────────────────────
export type ButtonVariant = 'primary' | 'secondary' | 'outline' | 'danger' | 'subtle';
export type ButtonSize = 'sm' | 'md' | 'lg';

export interface ButtonProps extends React.ButtonHTMLAttributes<HTMLButtonElement> {
  variant?: ButtonVariant;
  size?: ButtonSize;
}

export function Button({
  variant = 'primary',
  size = 'md',
  className = '',
  children,
  ...props
}: ButtonProps) {
  const variants: Record<ButtonVariant, string> = {
    primary: 'bg-sky-500 hover:bg-sky-400 text-black',
    secondary: 'bg-zinc-800 hover:bg-zinc-700 text-zinc-100',
    outline: 'border border-zinc-700 hover:bg-zinc-800 text-zinc-100',
    danger: 'bg-red-500 hover:bg-red-400 text-black',
    subtle: 'bg-zinc-900/60 hover:bg-zinc-900 border border-zinc-800 text-zinc-200',
  };
  const sizes: Record<ButtonSize, string> = {
    sm: 'px-2.5 py-1 text-sm',
    md: 'px-3.5 py-1.5 text-sm',
    lg: 'px-4 py-2 text-base',
  };
  return (
    <button
      className={cn('rounded transition-colors', variants[variant], sizes[size], className)}
      {...props}
    >
      {children}
    </button>
  );
}

// ── Input / Select / Textarea ────────────────────────────────────────────
export type InputProps = React.InputHTMLAttributes<HTMLInputElement>;

export function Input(props: InputProps) {
  return (
    <input
      {...props}
      className={cn(
        'bg-zinc-900 border border-zinc-800 rounded px-3 py-2 w-full focus:outline-none focus:ring-1 focus:ring-sky-400',
        props.className
      )}
    />
  );
}

export type SelectProps = React.SelectHTMLAttributes<HTMLSelectElement>;

export function Select(props: SelectProps) {
  return (
    <select
      {...props}
      className={cn(
        'bg-zinc-900 border border-zinc-800 rounded px-3 py-2 w-full focus:outline-none focus:ring-1 focus:ring-sky-400',
        props.className
      )}
    />
  );
}

export type TextareaProps = React.TextareaHTMLAttributes<HTMLTextAreaElement>;

export function Textarea(props: TextareaProps) {
  return (
    <textarea
      {...props}
      className={cn(
        'bg-zinc-900 border border-zinc-800 rounded px-3 py-2 w-full focus:outline-none focus:ring-1 focus:ring-sky-400',
        props.className
      )}
    />
  );
}

// ── Label ────────────────────────────────────────────────────────────────
export interface LabelProps {
  children?: React.ReactNode;
  className?: string;
}

export function Label({ children, className = '' }: LabelProps) {
  return <span className={cn('text-sm text-zinc-400', className)}>{children}</span>;
}

// ── Switch ───────────────────────────────────────────────────────────────
export interface SwitchProps {
  checked: boolean;
  onChange: (next: boolean) => void;
  className?: string;
}

export function Switch({ checked, onChange, className = '' }: SwitchProps) {
  return (
    <button
      type="button"
      aria-pressed={checked}
      onClick={() => onChange(!checked)}
      className={cn(
        'w-10 h-6 rounded-full border border-zinc-700 relative transition-colors',
        checked ? 'bg-emerald-500/30' : 'bg-zinc-800',
        className
      )}
    >
      <span
        className={cn(
          'w-4 h-4 rounded-full bg-white absolute top-1 transition-all',
          checked ? 'left-5' : 'left-1'
        )}
      ></span>
    </button>
  );
}

// ── Badge ────────────────────────────────────────────────────────────────
export type BadgeColor = 'zinc' | 'sky' | 'emerald' | 'red' | 'amber' | 'violet';

export interface BadgeProps {
  color?: BadgeColor;
  children?: React.ReactNode;
}

export function Badge({ color = 'zinc', children }: BadgeProps) {
  const map: Record<BadgeColor, string> = {
    zinc: 'bg-zinc-800 text-zinc-300',
    sky: 'bg-sky-600/20 text-sky-300',
    emerald: 'bg-emerald-600/20 text-emerald-300',
    red: 'bg-red-600/20 text-red-300',
    amber: 'bg-amber-600/20 text-amber-300',
    violet: 'bg-violet-600/20 text-violet-300',
  };
  return (
    <span className={cn('px-2 py-0.5 rounded text-xs', map[color] || map.zinc)}>{children}</span>
  );
}

// ── Spinner ──────────────────────────────────────────────────────────────
export interface SpinnerProps {
  size?: number;
}

export function Spinner({ size = 16 }: SpinnerProps) {
  const px = typeof size === 'number' ? size : 16;
  return (
    <svg
      width={px}
      height={px}
      viewBox="0 0 24 24"
      className="animate-spin text-sky-400"
      fill="none"
      xmlns="http://www.w3.org/2000/svg"
    >
      <circle
        className="opacity-25"
        cx="12"
        cy="12"
        r="10"
        stroke="currentColor"
        strokeWidth="4"
      ></circle>
      <path
        className="opacity-75"
        fill="currentColor"
        d="M4 12a8 8 0 018-8v4a4 4 0 00-4 4H4z"
      ></path>
    </svg>
  );
}

// ── Modal ────────────────────────────────────────────────────────────────
export interface ModalProps {
  open: boolean;
  onClose: () => void;
  title?: React.ReactNode;
  children?: React.ReactNode;
  footer?: React.ReactNode;
  className?: string;
}

export function Modal({
  open,
  onClose,
  title,
  children,
  footer = null,
  className = '',
}: ModalProps) {
  if (!open) return null;
  return (
    <div className="fixed inset-0 z-50 bg-black/50 backdrop-blur-sm flex items-center justify-center">
      <div
        className={cn(
          'bg-zinc-900 border border-zinc-800 rounded-lg w-[720px] max-w-[96vw] max-h-[90vh] overflow-auto',
          className
        )}
      >
        <div className="p-3 sm:p-4 border-b border-zinc-800 flex items-center justify-between">
          <div className="font-semibold">{title}</div>
          <button onClick={onClose} className="text-zinc-400 hover:text-zinc-200">
            ✕
          </button>
        </div>
        <div className="p-3 sm:p-4">{children}</div>
        {footer && (
          <div className="p-3 sm:p-4 border-t border-zinc-800 bg-zinc-950/50">{footer}</div>
        )}
      </div>
    </div>
  );
}

// ── KpiCard ──────────────────────────────────────────────────────────────
export interface KpiCardProps {
  label?: React.ReactNode;
  value?: React.ReactNode;
  hint?: React.ReactNode;
}

export function KpiCard({ label, value, hint }: KpiCardProps) {
  return (
    <div className="bg-zinc-900/70 border border-zinc-800 rounded-lg p-3">
      <div className="text-zinc-400 text-xs">{label}</div>
      <div className="text-xl font-bold">{value}</div>
      {hint && <div className="text-xs text-zinc-400 mt-0.5">{hint}</div>}
    </div>
  );
}
